/**
 * What the sign-in flow can refuse, said in a sentence a person can act on.
 *
 * The server never renders a stack trace: a failed check sends the browser to
 * `/signed-out?error=<code>` (`docs/API.md`, Sign-in). This turns that code
 * into an explanation. An unknown code is quoted rather than swallowed — the
 * server may grow a check this build has never heard of, and silence would
 * leave somebody staring at a button that "just does not work".
 */

import { SUPPORT_CHANNEL } from "../config";

/**
 * Explain why a sign-in did not complete.
 *
 * Args:
 *     code: The `error` query parameter the server redirected with.
 *     domain: The e-mail domain the server accepts, when it published one.
 *
 * Returns:
 *     One sentence, or null when there is nothing to explain.
 */
export function signInErrorMessage(
  code: string | null,
  domain: string | null,
): string | null {
  if (!code) {
    return null;
  }
  const accounts = domain ? `@${domain} accounts` : "Neuralk accounts";
  switch (code) {
    case "access_denied":
      return "Google did not complete that sign-in — it was cancelled or refused. You can try again.";
    case "domain_not_allowed":
    case "wrong_domain":
      return `That Google account is not a Neuralk one. The console only accepts ${accounts}; pick the right account and try again.`;
    case "email_not_verified":
      return "That Google account has no verified e-mail address, so the console cannot accept it.";
    case "invalid_state":
    case "expired":
      return "That sign-in could not be verified — it may have been started in another tab, or waited too long. Start it again.";
    case "rate_limited":
      return "Too many sign-in attempts. Wait a minute, then try again.";
    case "server_error":
      return `The console could not finish the sign-in. Try again; if it keeps happening, tell ${SUPPORT_CHANNEL}.`;
    default:
      return `Sign-in did not complete (${code}). Try again; if it keeps happening, tell ${SUPPORT_CHANNEL}.`;
  }
}
